import type { IndividualPumpCalculations } from "../calculationTypes";
import type { RecommendedOperatingRangeData } from "@/libs/calculations/pumps/pumpData";
import { computed } from "vue";
import { calculateMleEllipseMaxDepth } from "@/libs/calculations/cables";
import { PoundsPerCubicFoot } from "@/libs/units/density";
import {
  getRORForRPM,
  getDefaultPumpData,
  findCommonFlowMaxBrakeHPPoint,
  findCommonFlowMaxHeadPoint,
  getPumpCurveFrequencyRatio,
  getPeakHeadPointForPumpFt,
  getProducedHeadForPumpAtFlowRateFt,
  getPressureFromHeadPSIA
} from "@/libs/calculations/pumps/pumpData";
import { getShutInData } from "@/libs/calculations/pumps/shutInData";
import { getToleranceData } from "@/libs/calculations/pumps/toleranceData";
import type { PumpLimitsCtx } from "./context";

// Shut-in, burst, shaft and bolt load limits, and per-pump/taper calcs.
export const definePumpLimits = (ctx: PumpLimitsCtx) => {
  const {
    _equipment,
    _parameters,
    isScenarioEspShore,
    orderedIntakes,
    descendingOrderPumps,
    ascendingOrderPumps,
    descendingOrderSeals,
    primaryPump,
    primarySeal,
    basicData,
    fluidPropertiesAtPumpIntake,
    pumpIntakePressurePSIA,
    gasHeadRerateRatio,
    pumpData,
    requiredDischargePressurePSIA,
    producedDischargePressurePSIA,
    totalRequiredBrakeHP,
    pumpRequiredStages,
    pumpRecommendedViscosityCorrections
  } = ctx;

  const dischargeTemperatureF = computed(
    () => pumpData.value.dischargeTemperatureF
  );

  const thrustLoadPounds = computed(() => {
    const primaryPumpData =
      pumpData.value.individualPumpCalculations[primaryPump.value?.id];
    return primaryPumpData?.operatingThrustLoad || 0;
  });

  const rorData = computed(() =>
    ascendingOrderPumps.value.reduce<{
      [index: string]: RecommendedOperatingRangeData;
    }>((rorData, xrefPump) => {
      rorData[xrefPump.id] = getRORForRPM(
        xrefPump,
        ctx.operatingRPM.value,
        isScenarioEspShore.value
      );
      return rorData;
    }, {})
  );

  const shutInData = computed(() =>
    getShutInData(
      _equipment.value?.r_motor,
      ascendingOrderPumps.value,
      pumpData.value,
      ctx.primarySealBearingData.value.bearingRatingAtRPMLBS || 0,
      _parameters.value?.pump_hz,
      _parameters.value?.has_compression_pump as boolean,
      !isScenarioEspShore.value,
      ctx.motorData.value.motorVoltageCorrections
    )
  );

  const dischargeShutInBurstPressurePSIA = computed(() => {
    const shutIn =
      shutInData.value.dischargeTotalPressurePSIA +
      pumpIntakePressurePSIA.value;
    return Math.min(shutIn, totalMaxDischargePressurePSIA.value);
  });

  const shutInPressureRisePSIA = computed(
    () => shutInData.value.dischargeTotalPressurePSIA
  );

  const wellheadShutInBurstPressurePSIA = computed(
    () =>
      dischargeShutInBurstPressurePSIA.value -
      requiredDischargePressurePSIA.value +
      basicData.value.bottomTubingPressurePSIA
  );

  const shutInThrustLoadPounds = computed(() => {
    const primarySealId = primarySeal.value?.id;
    if (primarySealId) {
      return (
        ctx.individualSealCalculations.value[primarySealId]
          ?.shutInThrustLoadPounds || 0
      );
    }
    return 0;
  });

  const stdShutInBurstPercentage = computed(() => {
    const primaryPumpData = shutInData.value.pumps[primaryPump.value?.id];
    return primaryPumpData?.shutInBurstRatioStandard || 0;
  });

  const hsShutInBurstPercentage = computed(() => {
    const primaryPumpData = shutInData.value.pumps[primaryPump.value?.id];
    return primaryPumpData?.shutInBurstRatioHighStrength || 0;
  });

  const toleranceData = computed(() =>
    getToleranceData(
      ascendingOrderPumps.value,
      descendingOrderSeals.value,
      orderedIntakes.value,
      pumpIntakePressurePSIA.value,
      producedDischargePressurePSIA.value,
      ctx.operatingRPM.value
    )
  );

  const operatingBurstPressurePsia = computed(() =>
    Object.values(toleranceData.value.pumps).reduce((sum, p) => {
      return (sum += p.operatingBurstPressurePsia);
    }, 0)
  );

  const stdOperatingBurstPercentage = computed(() => {
    const primaryPumpData = toleranceData.value.pumps[primaryPump.value?.id];
    return primaryPumpData?.operatingBurstRatioStandard || 0;
  });

  const hsOperatingBurstPercentage = computed(() => {
    const primaryPumpData = toleranceData.value.pumps[primaryPump.value?.id];
    return primaryPumpData?.operatingBurstRatioHighStrength || 0;
  });

  const shaftPowerLimitStandardHP = computed(() => {
    const primaryPumpData = toleranceData.value.pumps[primaryPump.value?.id];
    return primaryPumpData?.shaftPowerLimitStandardHP || 0;
  });

  const shaftPowerLimitHighStrengthHP = computed(() => {
    const primaryPumpData = toleranceData.value.pumps[primaryPump.value?.id];
    return primaryPumpData?.shaftPowerLimitHighStrengthHP || 0;
  });

  const defaultPumpData = computed(() =>
    getDefaultPumpData(ascendingOrderPumps.value)
  );

  const averageViscosityCP = computed(
    () =>
      (fluidPropertiesAtPumpIntake.value.fluidViscosityCP +
        (pumpData.value?.individualPumpCalculations?.[
          descendingOrderPumps.value?.[0]?.id
        ]?.dischargeStageCalculations?.dischargeFluidPropertiesContext
          .fluidProperties?.fluidViscosityCP || 0)) /
      2
  );

  const averageSpecificGravity = computed(
    () =>
      (fluidPropertiesAtPumpIntake.value.fluidSpecificGravity +
        (pumpData.value?.individualPumpCalculations?.[
          descendingOrderPumps.value?.[0]?.id
        ]?.dischargeStageCalculations?.dischargeFluidPropertiesContext
          .fluidProperties?.fluidSpecificGravity || 0)) /
      2
  );

  const averageLiquidSpecificGravity = computed(
    () =>
      (fluidPropertiesAtPumpIntake.value.liquidSpecificGravity +
        (pumpData.value?.individualPumpCalculations?.[
          descendingOrderPumps.value?.[0]?.id
        ]?.dischargeStageCalculations?.dischargeFluidPropertiesContext
          .fluidProperties?.liquidSpecificGravity || 0)) /
      2
  );

  const primaryPumpHousingInnerDiameter = computed(
    () => primaryPump.value?.r_pump?.housing_inner_diameter_inches || 0
  );

  const axialBoltLoadLbs = computed(() => {
    return (
      ((Math.PI * Math.pow(primaryPumpHousingInnerDiameter.value, 2)) / 4) *
      producedDischargePressurePSIA.value
    );
  });

  const axialBoltLoadShutInLbs = computed(() => {
    return (
      ((Math.PI * Math.pow(primaryPumpHousingInnerDiameter.value, 2)) / 4) *
      dischargeShutInBurstPressurePSIA.value
    );
  });

  const axialBoltLoadRatio = computed(() => {
    return (
      axialBoltLoadLbs.value /
        primaryPump.value?.r_pump?.max_bolt_load_pounds || 0
    );
  });

  const axialBoltLoadShutInRatio = computed(() => {
    return (
      axialBoltLoadShutInLbs.value /
        primaryPump.value?.r_pump?.max_bolt_load_pounds || 0
    );
  });

  const primaryMaxHeadPoint = computed(() => {
    if (!primaryPump.value) {
      return null;
    }

    return getPeakHeadPointForPumpFt(
      primaryPump.value,
      gasHeadRerateRatio.value,
      getPumpCurveFrequencyRatio(ctx.operatingRPM.value),
      _parameters.value?.pump_wear_head_correction_ratio || 1
    );
  });

  const individualPumpCalculations = computed(() => {
    const pumpCurveFrequencyRatio = getPumpCurveFrequencyRatio(
      ctx.operatingRPM.value
    );

    return ascendingOrderPumps.value.reduce<IndividualPumpCalculations>(
      (acc, xrefPump) => {
        const liftPerStageHeadFt =
          pumpData.value.individualPumpCalculations[xrefPump.id]
            .totalDynamicHeadFt / (xrefPump.total_stages || 1);

        const bhpPerStageHP =
          pumpData.value.individualPumpCalculations[xrefPump.id].totalBrakeHP /
          (xrefPump.total_stages || 1);

        const ellipseAngle = Math.atan(
          _equipment.value?.r_mle?.width_inches /
            2 /
            (xrefPump?.r_pump?.housing_outer_diameter_inches +
              _equipment.value?.r_mle?.depth_inches)
        );
        const mleMaxEllipseDepthIn = calculateMleEllipseMaxDepth(
          ellipseAngle,
          _equipment.value?.r_mle?.depth_inches,
          xrefPump?.r_pump?.housing_outer_diameter_inches,
          0,
          false
        );
        const maxCalculatedOuterDiameterIn =
          mleMaxEllipseDepthIn +
          xrefPump?.r_pump?.housing_outer_diameter_inches;

        const intakeFlowRateBBLD =
          pumpData.value.individualPumpCalculations[xrefPump.id]
            ?.intakeStageCalculations?.intakeFluidPropertiesContext
            .flowRatesInSitu?.fluidFlowRateBBLD || 0;

        const maxHeadPoint = getPeakHeadPointForPumpFt(
          xrefPump,
          gasHeadRerateRatio.value,
          pumpCurveFrequencyRatio,
          _parameters.value?.pump_wear_head_correction_ratio || 1
        );
        const maxHeadFeet = maxHeadPoint.headFeet;

        const operatingHeadFeet = getProducedHeadForPumpAtFlowRateFt(
          intakeFlowRateBBLD,
          xrefPump,
          gasHeadRerateRatio.value,
          pumpCurveFrequencyRatio,
          _parameters.value?.pump_wear_head_correction_ratio || 1
        );

        const maxHousingPressurePSIA = totalMaxPressurePSIA.value;

        acc[xrefPump.id] = {
          xrefPump,
          ...pumpData.value.individualPumpCalculations[xrefPump.id],
          ...pumpRecommendedViscosityCorrections.value[xrefPump.id],
          ...pumpRequiredStages.value[xrefPump.id],
          ...rorData.value[xrefPump.id],
          ...toleranceData.value.pumps[xrefPump.id],
          ...defaultPumpData.value.pumps[xrefPump.id],
          ...shutInData.value.pumps[xrefPump.id],
          liftPerStageHeadFt,
          bhpPerStageHP,
          standardShaftRatingRatio:
            totalRequiredBrakeHP.value /
            toleranceData.value.pumps[xrefPump.id].shaftPowerLimitStandardHP,
          highStrengthShaftRatingRatio:
            totalRequiredBrakeHP.value /
            toleranceData.value.pumps[xrefPump.id]
              .shaftPowerLimitHighStrengthHP,
          maxHeadFeet,
          maxHeadFlowRateBBLD: maxHeadPoint.flowRateBBLD,
          operatingHeadFeet,
          maxHousingPressurePSIA,
          maxCalculatedOuterDiameterIn,
          mleMaxEllipseDepthIn
        };
        return acc;
      },
      {}
    );
  });

  const taperTotalOperatingHeadFeet = computed(() => {
    // Primary pump for HPS is top-most (first added), so taper pumps are the remaining descending-order pumps.
    return descendingOrderPumps.value.slice(1).reduce((sum, xrefPump) => {
      return (
        sum +
        (individualPumpCalculations.value[xrefPump.id]?.operatingHeadFeet || 0)
      );
    }, 0);
  });

  const taperTotalMaxHeadFeet = computed(() => {
    if (!primaryMaxHeadPoint.value) {
      return 0;
    }

    const pumpCurveFrequencyRatio = getPumpCurveFrequencyRatio(
      ctx.operatingRPM.value
    );
    const primaryMaxHeadFlowRateBBLD = primaryMaxHeadPoint.value.flowRateBBLD;

    return descendingOrderPumps.value.slice(1).reduce((sum, xrefPump) => {
      return (
        sum +
        getProducedHeadForPumpAtFlowRateFt(
          primaryMaxHeadFlowRateBBLD,
          xrefPump,
          gasHeadRerateRatio.value,
          pumpCurveFrequencyRatio,
          _parameters.value?.pump_wear_head_correction_ratio || 1
        )
      );
    }, 0);
  });

  const totalMaxDischargeHeadPoint = computed(() => {
    const pumpCurveFrequencyRatio = getPumpCurveFrequencyRatio(
      ctx.operatingRPM.value
    );
    const pumpWearHeadCorrectionRatio =
      _parameters.value?.pump_wear_head_correction_ratio || 1;

    return findCommonFlowMaxHeadPoint(
      descendingOrderPumps.value,
      gasHeadRerateRatio.value,
      pumpCurveFrequencyRatio,
      pumpWearHeadCorrectionRatio
    );
  });

  const totalMaxHeadFeet = computed(
    () => totalMaxDischargeHeadPoint.value.headFeet
  );

  const totalMaxBrakeHP = computed(() => {
    if (!descendingOrderPumps.value.length) {
      return 0;
    }

    const pumpCurveFrequencyRatio = getPumpCurveFrequencyRatio(
      ctx.operatingRPM.value
    );
    const fluidSpecificGravity =
      averageSpecificGravity.value > 0
        ? averageSpecificGravity.value
        : fluidPropertiesAtPumpIntake.value.fluidSpecificGravity > 0
          ? fluidPropertiesAtPumpIntake.value.fluidSpecificGravity
          : 0;
    const pumpWearBHPCorrectionRatio =
      _parameters.value?.pump_wear_brake_horsepower_correction_ratio || 1;

    return findCommonFlowMaxBrakeHPPoint(
      descendingOrderPumps.value,
      pumpCurveFrequencyRatio,
      fluidSpecificGravity,
      pumpWearBHPCorrectionRatio
    ).brakeHorsepowerHP;
  });

  const totalMaxPressurePSIA = computed(() => {
    const sg = isScenarioEspShore.value
      ? averageLiquidSpecificGravity.value
      : averageSpecificGravity.value;
    return getPressureFromHeadPSIA(
      totalMaxHeadFeet.value,
      PoundsPerCubicFoot.toThisUnit(sg)
    );
  });

  const totalMaxDischargePressurePSIA = computed(
    () => totalMaxPressurePSIA.value + pumpIntakePressurePSIA.value
  );

  return {
    dischargeTemperatureF,
    thrustLoadPounds,
    rorData,
    shutInData,
    dischargeShutInBurstPressurePSIA,
    shutInPressureRisePSIA,
    wellheadShutInBurstPressurePSIA,
    shutInThrustLoadPounds,
    stdShutInBurstPercentage,
    hsShutInBurstPercentage,
    toleranceData,
    operatingBurstPressurePsia,
    stdOperatingBurstPercentage,
    hsOperatingBurstPercentage,
    shaftPowerLimitStandardHP,
    shaftPowerLimitHighStrengthHP,
    defaultPumpData,
    averageViscosityCP,
    averageSpecificGravity,
    averageLiquidSpecificGravity,
    primaryPumpHousingInnerDiameter,
    axialBoltLoadLbs,
    axialBoltLoadShutInLbs,
    axialBoltLoadRatio,
    axialBoltLoadShutInRatio,
    primaryMaxHeadPoint,
    individualPumpCalculations,
    taperTotalOperatingHeadFeet,
    taperTotalMaxHeadFeet,
    totalMaxDischargeHeadPoint,
    totalMaxHeadFeet,
    totalMaxBrakeHP,
    totalMaxPressurePSIA,
    totalMaxDischargePressurePSIA
  };
};

export type PumpLimitsCalcs = ReturnType<typeof definePumpLimits>;
